/**
 * (owner, Sep 22: "could a website inside the browser be an actual html page? It would make building new in-game websites
 * a lot easier — or would that expose something that players could exploit?") Surf's HTML sites: a store with a `site`
 * in content is a real page, `content/sites/<site>/index.html`, shown in a sandboxed iframe laid over the phone's screen
 * while the store is open (docs/DESIGN_STUFF.md "HTML sites").
 *
 * Safe by three rules. (1) Only shipped pages: the src is `/sites/<site>/index.html` from the game's own origin — never a
 * URL a player typed or another player posted. (2) The sandbox: `allow-scripts` without `allow-same-origin` gives the page
 * an opaque origin — no cookies, no storage, no token, no navigating the game; the page's own CSP (`default-src 'none'`)
 * lets it fetch nothing. (3) The server stays the authority: the bridge below only sends requests the server validates
 * exactly as the canvas cart's (`Game.orderFor`): a tampered page could order what it could order anyway, with the
 * player's own money.
 *
 * The bridge is postMessage both ways. The page → the game: `{ sprawl: 'ready' }` (send me the state), `{ sprawl: 'order',
 * items, to }`, `{ sprawl: 'close' }` (back to the bookmarks), `{ sprawl: 'note', text }` (a line on the HUD). The game →
 * the page: `{ sprawl: 'state', store, catalogue: [{ id, name, price, img }], places: [{ key, name }], orders, cash, bank }` (a listings
 * site adds `listings` — game/listings.ts; the page may answer `{ sprawl: 'nav', x, y, name }` to set the navigator on one)
 * on ready, after an order, and every two seconds while open (the orders' minutes). Images are the props' sprites as
 * data URLs, drawn once per store.
 */
import type { RenderState } from '../engine/render-state';
import type { Renderer } from '../engine/renderer';
import { shelfPrice } from './stuff';
import { listings } from './listings';

export class SiteFrame {
  private el: HTMLIFrameElement;
  private store?: string;
  private lastState = 0;
  private imgs = new Map<string, string>();
  constructor(private renderer: Renderer, private onAct: (store: string, msg: { sprawl: string; items?: unknown; to?: unknown; text?: unknown; x?: unknown; y?: unknown; name?: unknown; key?: unknown }) => void) {
    this.el = document.getElementById('site') as HTMLIFrameElement;
    window.addEventListener('message', (e) => {
      if (!this.store || e.source !== this.el.contentWindow) return; // (only the frame we opened, only while a store is open)
      const m = e.data as { sprawl?: unknown } | null;
      if (!m || typeof m !== 'object' || typeof m.sprawl !== 'string') return;
      this.onAct(this.store, m as { sprawl: string });
    });
  }
  get open(): string | undefined { return this.store; }
  /** show the store's page over this screen rect (CSS pixels); the src changes only when the store does */
  show(storeId: string, site: string, rect: { x: number; y: number; w: number; h: number }, state: RenderState): void {
    if (this.store !== storeId) { this.store = storeId; this.imgs.clear(); this.el.src = `/sites/${encodeURIComponent(site)}/index.html`; this.lastState = 0; }
    this.el.style.left = `${Math.round(rect.x)}px`; this.el.style.top = `${Math.round(rect.y)}px`; this.el.style.width = `${Math.round(rect.w)}px`; this.el.style.height = `${Math.round(rect.h)}px`;
    if (this.el.style.display !== 'block') this.el.style.display = 'block'; // (the display, not `hidden`: an inline display beats the attribute — the owner saw a black box stay after leaving the store)
    if (performance.now() - this.lastState > 2000) this.post(state);
  }
  hide(): void { if (this.el.style.display !== 'none') this.el.style.display = 'none'; if (this.store) { this.store = undefined; this.el.src = 'about:blank'; } }
  /** the state the page draws from */
  post(state: RenderState): void {
    const id = this.store; if (!id) return;
    const store = state.reg.get('store', id); if (!store) return;
    this.lastState = performance.now();
    const catalogue = store.sells.map((pid) => { const def = state.reg.get('prop', pid); if (!def) return undefined; return { id: pid, name: def.name, price: shelfPrice(state.reg, store, pid), img: this.image(pid, def.sprite) }; }).filter((x): x is NonNullable<typeof x> => !!x);
    const msg = { sprawl: 'state', store: { id: store.id, name: store.name, tagline: store.tagline, color: store.color, url: store.url, delivery: store.delivery, fee: store.delivery.fee }, catalogue, places: state.myProps().filter((p) => p.key).map((p) => ({ key: p.key!, name: p.name })), orders: (state.orders?.() ?? []).map((o) => ({ store: o.store, n: o.n, to: o.to, left: o.left })), cash: state.hot(), bank: state.bank() };
    if (store.lists === 'property') (msg as { listings?: unknown }).listings = listings(state); // (owner, Sep 22) a listings site: what is on the market around the player
    this.el.contentWindow?.postMessage(msg, '*'); // ('*': the sandboxed page has an opaque origin — nothing else can be named; the frame is ours and only it gets this)
  }
  private image(id: string, sprite: Parameters<Renderer['icon']>[1]): string | undefined {
    const hit = this.imgs.get(id); if (hit !== undefined) return hit || undefined;
    let url = '';
    try {
      const c = this.renderer.icon(`p:${id}`, sprite);
      if ('toDataURL' in c) url = (c as HTMLCanvasElement).toDataURL();
      else { const t = document.createElement('canvas'); t.width = (c as OffscreenCanvas).width; t.height = (c as OffscreenCanvas).height; t.getContext('2d')?.drawImage(c as OffscreenCanvas, 0, 0); url = t.toDataURL(); }
    } catch { url = ''; }
    this.imgs.set(id, url);
    return url || undefined;
  }
}
